/**
 * Enum + slug constants shared between API and Web.
 *
 * The string values MUST stay in sync with the Prisma enums in
 * apps/api/prisma/schema.prisma and the seed data in apps/api/prisma/seed.ts.
 * Keeping these as `as const` objects (instead of TS `enum`) gives stable
 * structural typing across packages without the runtime ambiguity of TS enums.
 */

// ─── DB-backed enums (mirror prisma/schema.prisma) ──────────────────────────

export const AccessLevel = {
  EMPLOYEE: 'EMPLOYEE',
  MANAGER: 'MANAGER',
  ADMIN: 'ADMIN',
  SUPERVISOR: 'SUPERVISOR',
} as const;
export type AccessLevel = (typeof AccessLevel)[keyof typeof AccessLevel];

export const UserStatus = {
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
} as const;
export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];

export const WorkloadSource = {
  WEB: 'WEB',
  VK_TEAMS: 'VK_TEAMS',
  API: 'API',
  /**
   * Ф5 · В8-b.2 (миграция 060, Р-88). Довод — в `schema.prisma`, у самого
   * значения; здесь он НЕ пересказывается: два текста об одном решении однажды
   * разойдутся.
   *
   * ⚠️ Строка обязательна, а не желательна. Правило R7
   * (`infra/scripts/check-registration-sets.mjs`) сверяет множества значений
   * схемы и зеркала В ОБЕ СТОРОНЫ, и без неё гейт краснеет: «значения есть в
   * schema.prisma, но НЕТ в зеркале». Прецедент прожил незамеченным —
   * `EMERGENCY_ADMIN_RESET_ISSUED` в `AuditAction` ниже.
   */
  TELEGRAM: 'TELEGRAM',
} as const;
export type WorkloadSource = (typeof WorkloadSource)[keyof typeof WorkloadSource];

export const AuditAction = {
  INSERT: 'INSERT',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  /**
   * Аварийная выдача доступа супервайзером. Значение живёт в схеме с миграции
   * 008; в этом зеркале его НЕ БЫЛО до 06.08.2026 — расхождение нашёл круг
   * ревью Ф2 · В1, и оно прожило незамеченным ровно потому, что согласованность
   * зеркал не проверялась ничем, кроме комментария.
   *
   * Вреда не случилось только потому, что это зеркало нигде не используется:
   * иначе законная строка аудита с этим действием давала бы 500 при разборе
   * ответа. Теперь согласованность проверяет правило R7
   * (`infra/scripts/check-registration-sets.mjs`) — оно выводит ожидаемое из
   * `schema.prisma`, а не из второго рукописного списка.
   */
  EMERGENCY_ADMIN_RESET_ISSUED: 'EMERGENCY_ADMIN_RESET_ISSUED',
} as const;
export type AuditAction = (typeof AuditAction)[keyof typeof AuditAction];

export const ProjectStatus = {
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
  COMPLETED: 'COMPLETED',
} as const;
export type ProjectStatus = (typeof ProjectStatus)[keyof typeof ProjectStatus];

// ─── Role taxonomy (RTF L9-17) ──────────────────────────────────────────────
//
// Slugs are stable identifiers used by seed data and lookup logic. Display
// names live in i18n catalogs (Wave 7); do not derive UI labels from slugs.

export const RoleSlug = {
  EMPLOYEE: 'EMPLOYEE',
  LEAD_ENGINEER: 'LEAD_ENGINEER',
  /**
   * Block 2 «Трекер объектов» Этап 01a (§3.4 / KD-4 → A). Plain «Инженер»
   * position — EMPLOYEE tier, like LEAD_ENGINEER. Introduced as a `Role` row by
   * migration 037. This compile-time entry is REQUIRED: `visibleAssignableRoles`
   * (apps/web/.../visible-roles.ts) filters the `GET /roles` list through the
   * `ROLE_ACCESS_LEVEL` map, so an unmapped slug (`undefined` tier) is silently
   * hidden from the dropdown whatever the API returns.
   */
  ENGINEER: 'ENGINEER',
  DEPT_HEAD: 'DEPT_HEAD',
  GIP: 'GIP',
  /**
   * Feature 1 «Помощник ГИПа» (Wave 1). An assistant operates on behalf of a
   * single GIP (the binding lives in `User.assistantOfUserId`). ADMIN tier —
   * same access surface as the GIP they assist; behaviour (acts-as-GIP scoping)
   * lands in later waves.
   */
  ASSISTANT_GIP: 'ASSISTANT_GIP',
  CEO: 'CEO',
  TECH_DIRECTOR: 'TECH_DIRECTOR',
  BIM_MANAGER: 'BIM_MANAGER',
  SYSADMIN: 'SYSADMIN',
} as const;
export type RoleSlug = (typeof RoleSlug)[keyof typeof RoleSlug];

/**
 * Mapping from each role slug to its access tier. The seed script (2.7) uses
 * this to populate the `Role.accessLevel` column. Keep in sync with RTF +
 * decisions.md (D7 / role taxonomy).
 *
 * MANAGER tier corresponds to "руководитель отдела (РО)". DEPT_HEAD is the
 * legacy alias and currently shares the MANAGER tier; both rows get seeded so
 * existing imports keep working — Phase 2 cleanup may merge them.
 */
export const ROLE_ACCESS_LEVEL: Readonly<Record<RoleSlug, AccessLevel>> = {
  [RoleSlug.EMPLOYEE]: AccessLevel.EMPLOYEE,
  [RoleSlug.LEAD_ENGINEER]: AccessLevel.EMPLOYEE,
  [RoleSlug.ENGINEER]: AccessLevel.EMPLOYEE,
  [RoleSlug.DEPT_HEAD]: AccessLevel.MANAGER,
  [RoleSlug.GIP]: AccessLevel.ADMIN,
  [RoleSlug.ASSISTANT_GIP]: AccessLevel.ADMIN,
  [RoleSlug.CEO]: AccessLevel.SUPERVISOR,
  [RoleSlug.TECH_DIRECTOR]: AccessLevel.ADMIN,
  [RoleSlug.BIM_MANAGER]: AccessLevel.ADMIN,
  [RoleSlug.SYSADMIN]: AccessLevel.SUPERVISOR,
};

/**
 * «Сквозные» должности уровня `ADMIN` — пишут в ЛЮБОЙ объект (Р-84, «ГИП
 * вправе писать только в свои объекты», раздел «Что принято»): техдиректор и
 * BIM-менеджер. `GIP` и `ASSISTANT_GIP` здесь нет намеренно — они пишут только
 * в объект, который ведёт сам ГИП.
 *
 * Блок 2 · Ф8 · В-О2 (долг #352). До волны перечень жил в двух местах без
 * сверки между ними: сервер — `common/rbac/project-write.helper.ts`, веб —
 * выражение в `computeRbac` (`projects-detail.page.tsx`). Расхождение дало бы
 * кнопку, ведущую в отказ, либо право без кнопки. Теперь источник один.
 *
 * ⚠️ ПЕРЕЧЕНЬ РАЗРЕШАЮЩИЙ: новая должность уровня `ADMIN` сквозной НЕ
 * становится, пока её не внесут сюда.
 */
export const CROSS_PROJECT_ADMIN_ROLES: readonly RoleSlug[] = [
  RoleSlug.TECH_DIRECTOR,
  RoleSlug.BIM_MANAGER,
];

/**
 * Сквозная ли должность. Принимает и «должности нет» (образ API без поля,
 * незагруженный профиль) — это «не сквозная»: признак записи гаснет, а не
 * расширяется.
 *
 * ⚠️ ТОЛЬКО ДОЛЖНОСТЬ. Уровень доступа вызывающий проверяет сам, вместе с
 * ней: отказ «не администратор» не должен держаться на свойстве справочника.
 */
export function isCrossProjectAdminRole(roleSlug: string | null | undefined): boolean {
  return (
    roleSlug !== null &&
    roleSlug !== undefined &&
    (CROSS_PROJECT_ADMIN_ROLES as readonly string[]).includes(roleSlug)
  );
}

// ─── Department taxonomy (RTF L19-23) ───────────────────────────────────────

export const DepartmentSlug = {
  OViK: 'OViK', // Отопление и вентиляция
  VK: 'VK', // Водоснабжение и канализация
  EOM: 'EOM', // Электроснабжение
  SS: 'SS', // Слаботочные системы
  BIM: 'BIM', // BIM-моделирование
} as const;
export type DepartmentSlug = (typeof DepartmentSlug)[keyof typeof DepartmentSlug];

/**
 * Display names for departments — used by the seed script. UI translations
 * for end users will come from i18n catalogs (Wave 7); these strings are the
 * canonical Russian names per RTF L19-23.
 */
export const DEPARTMENT_NAMES: Readonly<Record<DepartmentSlug, string>> = {
  [DepartmentSlug.OViK]: 'Отопление и вентиляция',
  [DepartmentSlug.VK]: 'Водоснабжение и канализация',
  [DepartmentSlug.EOM]: 'Электроснабжение',
  [DepartmentSlug.SS]: 'Слаботочные системы',
  [DepartmentSlug.BIM]: 'BIM-моделирование',
};

/**
 * Display names for roles — used by the seed script. Russian per RTF L9-17.
 */
export const ROLE_NAMES: Readonly<Record<RoleSlug, string>> = {
  [RoleSlug.EMPLOYEE]: 'Сотрудник',
  [RoleSlug.LEAD_ENGINEER]: 'Ведущий инженер',
  [RoleSlug.ENGINEER]: 'Инженер',
  [RoleSlug.DEPT_HEAD]: 'Руководитель отдела',
  [RoleSlug.GIP]: 'ГИП',
  [RoleSlug.ASSISTANT_GIP]: 'Помощник ГИПа',
  [RoleSlug.CEO]: 'Генеральный директор',
  [RoleSlug.TECH_DIRECTOR]: 'Технический директор',
  [RoleSlug.BIM_MANAGER]: 'BIM-менеджер',
  [RoleSlug.SYSADMIN]: 'Системный администратор',
};
